/**
 * ParentInvoicesSection — parent portal invoice list (Phase AC.2).
 * Displays issued + paid invoices with View / Download / Pay Now
 * (Pay Now is a stub in AC.2 — wires to Mollie link generation later).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const fmtMoney = (v, currency = 'GBP') => (currency === 'GBP' ? '£' : '') + Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-GB') : '—';

const STATUS_STYLE = {
  issued: { label: 'Unpaid', bg: 'hsl(45 95% 92%)',  fg: 'hsl(35 80% 30%)' },
  paid:   { label: 'Paid',   bg: 'hsl(140 60% 92%)', fg: 'hsl(140 45% 26%)' },
};

export function ParentInvoicesSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.parentListInvoices();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err?.message || 'Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    let due = 0, paid = 0;
    for (const r of rows) {
      if (r.status === 'issued') due += Number(r.total || 0);
      else if (r.status === 'paid') paid += Number(r.total || 0);
    }
    return { due, paid };
  }, [rows]);

  const openPdf = async (r) => {
    setBusy((b) => ({ ...b, [r._id]: true }));
    try {
      const blob = await api.parentGetInvoicePdf(r._id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(err?.message || 'Open failed'); }
    finally { setBusy((b) => ({ ...b, [r._id]: false })); }
  };
  const downloadPdf = async (r) => {
    setBusy((b) => ({ ...b, [r._id]: true }));
    try {
      const blob = await api.parentGetInvoicePdf(r._id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `invoice-${r.invoice_number || r._id.slice(0, 8)}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(err?.message || 'Download failed'); }
    finally { setBusy((b) => ({ ...b, [r._id]: false })); }
  };
  const payNow = async (r) => {
    setBusy((b) => ({ ...b, [r._id]: true }));
    try {
      const res = await api.parentPayInvoiceMollie(r._id);
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error('No checkout URL returned. Please try again.');
      }
    } catch (err) {
      toast.error(err?.message || 'Card payment unavailable — please use bank transfer.');
    } finally { setBusy((b) => ({ ...b, [r._id]: false })); }
  };

  // Statement download — defaults to the current calendar month.
  const [statementBusy, setStatementBusy] = useState(false);
  const downloadStatement = async () => {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
    setStatementBusy(true);
    try {
      const blob = await api.parentGetStatementPdf({
        date_from: first.toISOString(),
        date_to: last.toISOString(),
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(err?.message || 'Statement failed'); }
    finally { setStatementBusy(false); }
  };

  return (
    <div className="card" data-testid="parent-invoices-card" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0 }}>🧾 My invoices</h3>
          <p style={{ margin: '0.15rem 0 0', color: 'hsl(var(--muted-foreground))', fontSize: '0.82rem' }}>
            {rows.length === 0
              ? 'Your invoices from the nursery will appear here.'
              : <>Outstanding <strong>{fmtMoney(totals.due)}</strong> · Paid <strong>{fmtMoney(totals.paid)}</strong></>}
          </p>
        </div>
        {rows.length > 0 && (
          <button type="button" className="btn btn-ghost" onClick={downloadStatement} disabled={statementBusy} data-testid="parent-statement-download" style={{ fontSize: '0.82rem' }}>
            {statementBusy ? 'Building…' : '📊 This month\'s statement'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading" data-testid="parent-invoices-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="parent-invoices-empty">
          No invoices yet — you'll see them here once the nursery issues one.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }} data-testid="parent-invoices-table">
            <thead>
              <tr style={{ borderBottom: '2px solid hsl(var(--border))', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Number</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Issued</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Due</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Status</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = STATUS_STYLE[r.status] || STATUS_STYLE.issued;
                const canPay = r.status === 'issued';
                return (
                  <tr key={r._id} data-testid={`parent-invoice-row-${r._id}`} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                    <td style={{ padding: '0.5rem', fontWeight: 600 }}>{r.invoice_number}</td>
                    <td style={{ padding: '0.5rem' }}>{fmtDate(r.issue_date)}</td>
                    <td style={{ padding: '0.5rem' }}>{fmtDate(r.due_date)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtMoney(r.total, r.currency)}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, background: s.bg, color: s.fg, fontSize: '0.72rem', fontWeight: 700 }}>{s.label}</span>
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => openPdf(r)} disabled={busy[r._id]} className="btn btn-ghost" style={{ fontSize: '0.78rem', padding: '0.2rem 0.55rem' }} data-testid={`parent-invoice-view-${r._id}`}>📄 View</button>
                      {' '}
                      <button type="button" onClick={() => downloadPdf(r)} disabled={busy[r._id]} className="btn btn-ghost" style={{ fontSize: '0.78rem', padding: '0.2rem 0.55rem' }} data-testid={`parent-invoice-download-${r._id}`}>⬇ PDF</button>
                      {canPay && (
                        <>
                          {' '}
                          <button type="button" onClick={() => payNow(r)} disabled={busy[r._id]} className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '0.25rem 0.7rem' }} data-testid={`parent-invoice-pay-${r._id}`}>💳 Pay Now</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ParentInvoicesSection;
